/**
 * Shared "what should I do next on this bid?" state machine.
 *
 * Two callers:
 *   - The Summary page's NextStepCard (rich data — full project + sections + matrix + coverage).
 *   - The Dashboard project list (aggregated data from /projects/dashboard-state — counts only).
 *
 * Both produce a NextStep result. Behavior is identical; only the input
 * shape differs. The aggregated-data branch produces a slightly less
 * detailed message (no specific section names) but otherwise matches.
 */

export interface NextStepInput {
  projectId: string;
  hasRfpFile: boolean;
  hasRawRfpText: boolean;
  hasSummary: boolean;
  hasFormat: boolean;
  status: string;
  sectionCount: number;
  sectionsComplete: number;
  sectionsMissingProse: number;
  sectionsMissingAsset: number;
  matrixTotal: number;
  deliverableTotal: number;
  deliverableCovered: number;
}

export interface NextStepResult {
  /** Short label suitable for a single-line indicator on a Dashboard card. */
  short: string;
  /** Longer title used as the headline on the full-width Summary card. */
  title: string;
  /** Optional one-paragraph explanation (only used by the Summary card). */
  description?: string;
  /** Where the action takes the user. Empty when no action applies. */
  action?: { label: string; href: string };
  /** Visual tone — controls icon color / accent. */
  tone: "info" | "warn" | "ok" | "muted";
}

export function computeNextStep(input: NextStepInput): NextStepResult {
  const { projectId } = input;
  const href = (suffix: string) => `/projects/${projectId}${suffix}`;

  // 1. No RFP source.
  if (!input.hasRfpFile && !input.hasRawRfpText) {
    return {
      short: "Upload the RFP",
      title: "Upload the RFP to get started",
      description:
        "Drop the RFP source so the parser can extract requirements, summary, and a starter outline.",
      action: { label: "Upload", href: href("/summary") },
      tone: "warn",
    };
  }

  // 2. RFP file but parse hasn't finished.
  if (input.hasRfpFile && !input.hasRawRfpText) {
    return {
      short: "Parsing…",
      title: "Parsing the RFP…",
      description: "Extracting text, summary, and requirements. Usually takes 30–60 seconds.",
      tone: "muted",
    };
  }

  // 3. Summary not generated.
  if (!input.hasSummary) {
    return {
      short: "Read the RFP",
      title: "Read the RFP",
      description: "Generate the summary so the rest of the system has context to work with.",
      action: { label: "Open summary", href: href("/summary") },
      tone: "warn",
    };
  }

  // 4. Submission format not picked.
  if (!input.hasFormat) {
    return {
      short: "Pick submission format",
      title: "Pick the submission format",
      description:
        "Word doc, presentation, or both. Drives outline suggestions, AI prompt tuning, and export options.",
      action: { label: "Choose format", href: href("/drafts") },
      tone: "warn",
    };
  }

  // 5. No sections — outline not approved.
  if (input.sectionCount === 0) {
    return {
      short: "Approve outline",
      title: "Approve a response outline",
      description:
        input.matrixTotal > 0
          ? "AI can propose a section structure based on the parsed requirements. Review, edit, approve."
          : "Add sections manually or run the parser to extract requirements first.",
      action: { label: "Open RFP Response", href: href("/drafts") },
      tone: "warn",
    };
  }

  // 6. Coverage gap — deliverable requirements without a linked section.
  if (input.deliverableTotal > 0) {
    const uncovered = input.deliverableTotal - input.deliverableCovered;
    if (uncovered > 0) {
      return {
        short: `${uncovered} requirement${uncovered === 1 ? "" : "s"} uncovered`,
        title: `${uncovered} requirement${uncovered === 1 ? "" : "s"} not yet covered`,
        description:
          "Some deliverable requirements have no section assigned to address them. Open a section and link the requirement, or add a new section.",
        action: { label: "Review response", href: href("/drafts") },
        tone: "warn",
      };
    }
  }

  // 7. Sections without content.
  const missingTotal = input.sectionsMissingProse + input.sectionsMissingAsset;
  if (missingTotal > 0) {
    const parts: string[] = [];
    if (input.sectionsMissingProse > 0)
      parts.push(
        `${input.sectionsMissingProse} draft${input.sectionsMissingProse === 1 ? "" : "s"} missing prose`,
      );
    if (input.sectionsMissingAsset > 0)
      parts.push(
        `${input.sectionsMissingAsset} asset slot${input.sectionsMissingAsset === 1 ? "" : "s"} empty`,
      );
    return {
      short: `${missingTotal} section${missingTotal === 1 ? "" : "s"} missing content`,
      title: "Build out the remaining sections",
      description: parts.join(" · ") + ".",
      action: { label: "Open RFP Response", href: href("/drafts") },
      tone: "warn",
    };
  }

  // 8. Drafts present but not finalized.
  const notFinal = input.sectionCount - input.sectionsComplete;
  if (notFinal > 0) {
    return {
      short: `${notFinal} of ${input.sectionCount} still in draft`,
      title: "Review and finalize",
      description: `${notFinal} of ${input.sectionCount} section${input.sectionCount === 1 ? "" : "s"} still in draft. Walk the team through review, then mark each Final.`,
      action: { label: "Open RFP Response", href: href("/drafts") },
      tone: "warn",
    };
  }

  // 9. Submitted — capture outcome.
  if (input.status === "submitted") {
    return {
      short: "Capture outcome",
      title: "Capture the outcome",
      description:
        "Mark the bid won or lost so it feeds into the agency's reference library for the next bid.",
      tone: "muted",
    };
  }

  // 10. Closed.
  if (input.status === "won") {
    return { short: "Won", title: "Bid won", tone: "ok" };
  }
  if (input.status === "lost") {
    return { short: "Lost", title: "Bid lost", tone: "muted" };
  }

  // 11. Everything ready.
  return {
    short: "Ready to export",
    title: "Ready to export and submit",
    description: "All sections are final. Run a quick final check, then export the DOCX and ship.",
    action: { label: "Final check", href: href("/qa") },
    tone: "ok",
  };
}
